import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, CircleNotch, Warning, ArrowRight } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PecosBadge = ({ npi, isMainSection = false }) => {
  const [status, setStatus] = useState("loading"); // loading | enrolled | not_found | unavailable
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!npi) return;
    let cancelled = false;

    const fetchPecos = async () => {
      try {
        const res = await fetch(`${API_URL}/api/pecos/check/${npi}`);
        if (!res.ok) throw new Error("fetch failed");
        const json = await res.json();
        if (cancelled) return;
        setStatus(json.status);
        setData(json.data);
      } catch {
        if (!cancelled) setStatus("unavailable");
      }
    };
    fetchPecos();
    return () => { cancelled = true; };
  }, [npi]);

  if (status === "loading") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="pecos-section">
        <h3 className={`font-display font-semibold text-slate-900 ${isMainSection ? "text-lg mb-4" : "text-base mb-3"}`}>
          Medicare Enrollment (PECOS)
        </h3>
        <div className="flex items-center gap-2 text-slate-400">
          <CircleNotch className="w-5 h-5 animate-spin" />
          <span className="text-sm">Checking PECOS enrollment...</span>
        </div>
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="pecos-section">
        <h3 className={`font-display font-semibold text-slate-900 ${isMainSection ? "text-lg mb-4" : "text-base mb-3"}`}>
          Medicare Enrollment (PECOS)
        </h3>
        <div className="flex items-center gap-2 text-slate-400">
          <Warning className="w-5 h-5" />
          <span className="text-sm">PECOS status unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="pecos-section">
      <h3 className={`font-display font-semibold text-slate-900 ${isMainSection ? "text-lg mb-4" : "text-base mb-3"}`}>
        Medicare Enrollment (PECOS)
      </h3>
      {status === "enrolled" ? (
        <div>
          <div className="flex items-center gap-2 mb-3" data-testid="pecos-enrolled-badge">
            <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
              <CheckCircle className="w-4 h-4 text-emerald-600" weight="fill" />
              <span className="text-sm font-semibold text-emerald-700">PECOS Enrolled</span>
            </div>
          </div>
          <p className="text-sm text-slate-700 mb-2">
            <span className="font-medium">Eligible to Order & Refer in Medicare:</span>{" "}
            <span className="text-emerald-600 font-semibold">Yes</span>
          </p>
          {data && (
            <div className="flex flex-wrap gap-2 mt-2">
              {data.partb && <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">Part B</span>}
              {data.dme && <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">DME</span>}
              {data.hha && <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">HHA</span>}
              {data.pmd && <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">PMD</span>}
              {data.hospice && <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">Hospice</span>}
            </div>
          )}
          {isMainSection && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <Link
                to={`/pecos-lookup/npi/${npi}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-teal hover:text-teal/80 transition-colors"
                data-testid="pecos-detail-link"
              >
                View full PECOS details <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-2" data-testid="pecos-not-found-badge">
            <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-full px-3 py-1.5">
              <XCircle className="w-4 h-4 text-red-500" weight="fill" />
              <span className="text-sm font-semibold text-red-600">Not Found in PECOS</span>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            This provider was not found in the CMS Order & Referring dataset.
          </p>
          {isMainSection && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <Link
                to={`/pecos-lookup/npi/${npi}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-teal hover:text-teal/80 transition-colors"
                data-testid="pecos-detail-link"
              >
                Check PECOS lookup tool <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-3 border-t border-slate-100 pt-2">
        Data sourced from CMS Order & Referring dataset. Updated monthly.
      </p>
    </div>
  );
};

export default PecosBadge;
